// Thin fetch wrappers for the share-link public edge functions.
//
// Both endpoints are unauthenticated — the token in the URL is the auth
// credential. We pass the project's anon `apikey` only so the Supabase
// gateway accepts the call.

import type { SessionVariableDef, WorkflowGraph } from '@little-loop/shared-types';
import type { SnapshotCard } from '@little-loop/tutorial-player';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

function functionsBase(): string {
  if (!SUPABASE_URL) {
    throw new Error('VITE_SUPABASE_URL is not set.');
  }
  return `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1`;
}

function headers(): HeadersInit {
  if (!SUPABASE_ANON_KEY) {
    throw new Error('VITE_SUPABASE_ANON_KEY is not set.');
  }
  return {
    apikey: SUPABASE_ANON_KEY,
    'Content-Type': 'application/json',
  };
}

// ── Lookup ────────────────────────────────────────────────────────────────

export type LookupErrorCode =
  | 'invalid_format'
  | 'rate_limited'
  | 'not_found'
  | 'revoked'
  | 'expired'
  | 'unsupported_scope'
  | 'tutorial_not_found'
  | 'tutorial_unavailable'
  /** The safety canary fired for this recipient (unreviewed safety arm). */
  | 'tutorial_blocked';

/**
 * A published workflow snapshot, as `resolve_workflow_for_recipient` returns
 * it: forks already selected and pruned for this recipient, cards materialised,
 * outcomes and session-variable declarations pinned to the version.
 */
export interface LookupTutorial {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  topic_code: string | null;
  /** Pins the snapshot this play runs against. */
  workflow_version_id: string;
  version: number;
  graph: WorkflowGraph;
  /** Materialised card content keyed by `card_id`. */
  cards: Record<string, SnapshotCard>;
  session_variables: SessionVariableDef[];
  /**
   * Outcomes referenced by this version, snapshotted so an unknown code still
   * renders its label rather than crashing (ENG-160 tolerant decoding).
   */
  outcomes: LookupOutcome[];
  /** Canonical-key values for `{{token}}` interpolation. */
  variable_values: Record<string, string>;
  /**
   * Recipient-scoped facts. Routing and block visibility evaluate against
   * these — the same map the resolver used to prune forks.
   */
  facts: Record<string, string>;
}

export interface LookupOutcome {
  code: string;
  label: string;
  kind: 'success' | 'failure' | 'neutral';
  emoji: string | null;
  display_message: string | null;
  color_hex: string | null;
  sort_order?: number;
}

export interface VariableDefinition {
  name: string;
  display_name?: string;
  example_value?: string | null;
}

export type LookupResult =
  | {
      valid: true;
      recipient_first_name: string;
      manager_name: string;
      expires_at: string;
      already_completed: boolean;
      tutorial: LookupTutorial;
    }
  | {
      valid: false;
      error: LookupErrorCode;
      expires_at?: string;
      /** Set when `error === 'tutorial_blocked'`. */
      blocked_reason?: string;
    };

interface SupabaseEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export async function lookupShareLink(token: string): Promise<LookupResult> {
  const res = await fetch(
    `${functionsBase()}/share-link-lookup?token=${encodeURIComponent(token)}`,
    { method: 'GET', headers: headers() },
  );
  const body = (await res.json()) as SupabaseEnvelope<LookupResult>;
  if (!res.ok || !body.success || !body.data) {
    throw new Error(body.error?.message ?? `Lookup failed (${res.status})`);
  }
  return body.data;
}

// ── Complete ──────────────────────────────────────────────────────────────

export interface CompleteRequest {
  token: string;
  completion_time_seconds?: number;
  branch_path?: string[];
  // ENG-175: the share path now carries the play instead of discarding it.
  // `session_values` is a flat top-level map (engine-stringified) — the server
  // nests it under responses.session for storage.
  responses?: Record<string, string>;
  card_progress?: Array<{ card_id: string; view_count: number }>;
  session_values?: Record<string, string>;
  restarts?: number;
  outcome_code?: string | null;
  total_cards?: number;
  workflow_version_id?: string;
}

export interface CompleteResult {
  ok?: boolean;
  already_completed?: boolean;
  completed_at?: string;
  notified?: boolean;
}

export async function completeShareLink(req: CompleteRequest): Promise<CompleteResult> {
  const res = await fetch(`${functionsBase()}/share-link-complete`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(req),
  });
  const body = (await res.json()) as SupabaseEnvelope<CompleteResult>;
  if (!res.ok || !body.success) {
    throw new Error(body.error?.message ?? `Completion failed (${res.status})`);
  }
  return body.data ?? {};
}
